import { APIURL } from "./config";

/**
 * Assessment Slot Management API
 * Functions for managing assessment slots, candidate assignments, and slot status
 */

export async function getAllAssessmentSlots(
    filterQuery: string = "",
    searchQuery: string = "",
    dateFilter: string = "",
    token: string
) {
    const params = new URLSearchParams();
    if (filterQuery) params.append("filterQuery", filterQuery);
    if (searchQuery) params.append("searchQuery", searchQuery);
    if (dateFilter) params.append("dateFilter", dateFilter);

    const url = `${APIURL}/Roles/GetAllAssessmentSlots${params.toString() ? `?${params.toString()}` : ""}`;
    const res = await fetch(url, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });
    const assessmentSlots = await res.json();
    return assessmentSlots;
}

export const getAssessmentSlotStatus = async (candidateId: string, assessmentSlotId: string, token: string) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/${candidateId}/${assessmentSlotId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        if (!response.ok) throw new Error("Failed to fetch status");
        const json = await response.json();
        return json;
    } catch (error) {
        console.error('Error fetching AssessmentSlotStatus:', error);
        return { status: 'paused', statusCode: 0 };
    }
};

// Update status for all candidates in a slot
export const updateAssessmentSlotStatus = async (assessmentSlotId: string, statusCode: number, token: string) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/updateAll`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    AssessmentSlotId: assessmentSlotId,
                    AdminStatusCode: statusCode,
                }),
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error:', error);
        alert('Failed to update status');
    }
};

export const updateSingleCandidateAssessmentSlotStatus = async (
    candidateId: string,
    assessmentSlotId: string,
    statusCode: number,
    token: string
) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/updateSingle`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    CandidateId: candidateId,
                    AssessmentSlotId: assessmentSlotId,
                    AdminStatusCode: statusCode,
                }),
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error:', error);
        alert('Failed to update candidate status');
    }
};

export const updateBulkCandidateAssessmentSlotStatus = async (
    candidateIds: string[],
    assessmentSlotId: string,
    statusCode: number,
    token: string
) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/updateBulk`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    CandidateIds: candidateIds,
                    AssessmentSlotId: assessmentSlotId,
                    AdminStatusCode: statusCode,
                }),
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error:', error);
        alert('Failed to update candidates status');
    }
};

export const getAvailableSlotsForReassign = async (roleStatusId: string, token: string) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/availableSlots/${roleStatusId}`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching available slots:', error);
        return [];
    }
};

export const reassignCandidateSlot = async (
    candidateId: string,
    currentSlotId: string | null,
    newSlotId: string | null,
    cancelCurrentSlot: boolean,
    bookNewSlot: boolean,
    token: string
) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/reassign`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    CandidateId: candidateId,
                    CurrentSlotId: currentSlotId,
                    NewSlotId: newSlotId,
                    CancelCurrentSlot: cancelCurrentSlot,
                    BookNewSlot: bookNewSlot,
                }),
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error reassigning slot:', error);
        throw error;
    }
};

// Reset an assessment score
export const resetCandidateAssessmentScore = async (
    candidateId: string,
    roleStatusId: string,
    token: string
) => {
    try {
        const response = await fetch(
            `${APIURL}/AssessmentSlotStatus/resetCandidateAssessmentScore`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    CandidateId: candidateId,
                    RoleStatusId: roleStatusId,
                }),
            }
        );
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error resetting candidate assessment score:', error);
        throw error;
    }
};

export async function getCandidatesBySlot(
    slotId: string,
    filterQuery: string = "",
    searchQuery: string = "",
    token: string
) {
    const params = new URLSearchParams();
    if (filterQuery) params.append("filterQuery", filterQuery);
    if (searchQuery) params.append("searchQuery", searchQuery);

    const url = `${APIURL}/Roles/getCandidatesBySlot/${slotId}${params.toString() ? `?${params.toString()}` : ""}`;
    const res = await fetch(url, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });
    const data = await res.json();
    return data;
}

/**
 * Fetches the assessment slot for the candidate's CURRENT stage in the given role.
 * Returns null when the candidate has no assessment slot at their current stage
 * (e.g. they have been advanced to Interview or beyond).
 * Pass roleId so the backend can match the slot to the candidate's current role status.
 */
export const getActiveSlotForCandidate = async (
    candidateId: string,
    token: string,
    roleId?: string
): Promise<{
    assessmentSlotId: string;
    startTime: string;
    endTime: string;
    originalEndTime: string;
    extendedEndTime: string | null;
    adminStatus: number;
    windowWasReset: boolean;
    takenWithoutSeb: boolean;
    // Identify which role stage and which assessment this slot belongs to, so the candidate
    // profile can offer other slots for the SAME assessment when rebooking.
    roleStatusId: string | null;
    assessmentQuestionnaireId: string | null;
    assessmentName: string | null;
    directLink: string;
    sebLink: string;
} | null> => {
    try {
        const url = roleId
            ? `${APIURL}/AssessmentSlotStatus/activeSlot/${candidateId}?roleId=${roleId}`
            : `${APIURL}/AssessmentSlotStatus/activeSlot/${candidateId}`;
        const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) return null;
        return response.json();
    } catch {
        return null;
    }
};

/**
 * Resets the assessment window for a specific candidate.
 * The backend sets ExtendedEndTime = now + original slot duration and
 * broadcasts a WindowReset SignalR event to the candidate's browser.
 */
export const resetCandidateWindow = async (
    candidateId: string,
    assessmentSlotId: string,
    token: string
): Promise<{ success: boolean; newEndTime: string }> => {
    const response = await fetch(
        `${APIURL}/AssessmentSlotStatus/resetWindow`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                CandidateId: candidateId,
                AssessmentSlotId: assessmentSlotId,
            }),
        }
    );
    if (!response.ok) throw new Error('Failed to reset assessment window');
    return response.json();
};

/**
 * Approves a candidate held on the deliberate-reload limit. Server clears the pause and resets
 * the counter to 1 in one transaction, then tells the candidate's browser to resume with the
 * time they had left. The exam window is deliberately NOT extended.
 */
export const approveRefreshLimitResume = async (
    candidateId: string,
    assessmentSlotId: string,
    token: string,
    roleAssessmentMappingId?: string
): Promise<{ success: boolean }> => {
    const response = await fetch(
        `${APIURL}/AssessmentSlotStatus/approveRefreshLimitResume`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                CandidateId: candidateId,
                AssessmentSlotId: assessmentSlotId,
                RoleAssessmentMappingId: roleAssessmentMappingId ?? null,
            }),
        }
    );
    if (!response.ok) throw new Error('Failed to approve exam resume');
    return response.json();
};

/**
 * Resets the assessment window for multiple candidates in bulk.
 */
export const bulkResetCandidateWindows = async (
    candidateIds: string[],
    assessmentSlotId: string,
    token: string
): Promise<{ success: boolean; message: string }> => {
    const response = await fetch(
        `${APIURL}/AssessmentSlotStatus/bulkResetWindows`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                CandidateIds: candidateIds,
                AssessmentSlotId: assessmentSlotId,
            }),
        }
    );
    if (!response.ok) throw new Error('Failed to bulk reset assessment windows');
    return response.json();
};

/**
 * Sends the candidate the old-style plain (non-SEB) assessment link and marks
 * TakenWithoutSeb = true on their CandidateAssessmentSlot record.
 */
export const sendAssessmentLinkWithoutSeb = async (
    candidateId: string,
    assessmentSlotId: string,
    token: string
): Promise<{ success: boolean }> => {
    const response = await fetch(
        `${APIURL}/AssessmentSlotStatus/sendWithoutSeb`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                CandidateId: candidateId,
                AssessmentSlotId: assessmentSlotId,
            }),
        }
    );
    if (!response.ok) throw new Error('Failed to send assessment link without SEB');
    return response.json();
};

/**
 * Bulk version of sendAssessmentLinkWithoutSeb — mails the plain (non-SEB) link to
 * every selected candidate in one request. Returns per-candidate failures so the UI
 * can report a partial send instead of a blanket success.
 */
export const bulkSendAssessmentLinkWithoutSeb = async (
    candidateIds: string[],
    assessmentSlotId: string,
    token: string
): Promise<{
    success: boolean;
    sent: number;
    failed: number;
    failedCandidates: { candidateId: string; name: string; email: string }[];
}> => {
    const response = await fetch(
        `${APIURL}/AssessmentSlotStatus/bulkSendWithoutSeb`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                CandidateIds: candidateIds,
                AssessmentSlotId: assessmentSlotId,
            }),
        }
    );
    if (!response.ok) throw new Error('Failed to send assessment links without SEB');
    return response.json();
};

/**
 * Returns a map of { roleStatusId -> takenWithoutSeb } for every assessment slot
 * belonging to this candidate.  Optionally scoped to a specific role.
 * ScoreGrades calls this to determine the flag per score row individually.
 */
export const getTakenWithoutSebFlags = async (
    candidateId: string,
    token: string,
    roleId?: string
): Promise<Record<string, boolean>> => {
    const url = roleId
        ? `${APIURL}/AssessmentSlotStatus/takenWithoutSebFlags/${candidateId}?roleId=${roleId}`
        : `${APIURL}/AssessmentSlotStatus/takenWithoutSebFlags/${candidateId}`;
    try {
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return {};
        return response.json();
    } catch {
        return {};
    }
};
